import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useTranslation, Trans } from 'react-i18next';

const ServicesPage = () => {
  const { t } = useTranslation();
  const services = [
    {
      id: '01',
      title: t('services.items.01.title'),
      description: t('services.items.01.description'),
      items: t('services.items.01.list', { returnObjects: true }) as string[]
    },
    {
      id: '02',
      title: t('services.items.02.title'),
      description: t('services.items.02.description'),
      items: t('services.items.02.list', { returnObjects: true }) as string[]
    },
    {
      id: '03',
      title: t('services.items.03.title'),
      description: t('services.items.03.description'),
      items: t('services.items.03.list', { returnObjects: true }) as string[]
    },
    {
      id: '04',
      title: t('services.items.04.title'),
      description: t('services.items.04.description'),
      items: t('services.items.04.list', { returnObjects: true }) as string[]
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <title>{t('services.meta.title')}</title>
      <meta name="description" content={t('services.meta.description')} />
      <meta name="keywords" content={t('services.meta.keywords')} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": t('services.meta.title'),
          "description": t('services.meta.description'),
          "provider": {
            "@type": "Organization",
            "name": "Mood Agency",
            "url": "https://mood-agency.com"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": t('services.title'),
            "itemListElement": services.map(s => ({
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": s.title
              }
            }))
          }
        })}
      </script>
      <Navbar />

      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-white pt-32 pb-20">
        {/* Background Ripple Effect */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[80vw] h-[80vw] border border-gray-100 rounded-full absolute"></div>
          <div className="w-[60vw] h-[60vw] border border-gray-100 rounded-full absolute"></div>
          <div className="w-[40vw] h-[40vw] border border-gray-100 rounded-full absolute"></div>
        </div>
        
        <div className="relative z-20 w-full max-w-7xl mx-auto px-2 md:px-10">
          <span className="font-display text-[10px] tracking-[0.4em] text-gray-400 uppercase">
            {t('services.subtitle')}
          </span>
          <h1 className="font-display text-[12vw] leading-none font-bold text-gray-900 uppercase tracking-tighter mt-12 mb-12">
            {t('services.title')}<br />
            <span className="text-gray-300 italic">{t('services.highlight')}</span>
          </h1>
          <p className="font-sans text-xl text-gray-500 max-w-2xl leading-relaxed">
            {t('services.description')}
          </p>
        </div>
      </section>
      
      <section className="py-32 px-2 md:px-10 bg-white border-t border-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 gap-32">
            {services.map((service) => (
              <div key={service.id} className="group grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-1">
                  <span className="font-display text-2xl text-gray-200 group-hover:text-gray-900 transition-colors duration-500">
                    {service.id}
                  </span>
                </div>
                <div className="md:col-span-5">
                  <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight mb-8">
                    {service.title}
                  </h2>
                  <p className="font-sans text-lg text-gray-500 leading-relaxed mb-8">
                    {service.description}
                  </p>
                </div>
                <div className="md:col-span-6 flex flex-col justify-center">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-200 group-hover:bg-gray-900 transition-colors duration-500"></div>
                        <span className="font-display text-[10px] tracking-[0.2em] text-gray-400 uppercase group-hover:text-gray-900 transition-colors duration-500">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-32 px-2 md:px-10 bg-gray-50 flex items-center justify-center text-center">
        <div className="max-w-3xl">
          <h2 
            className="font-display text-4xl md:text-6xl font-bold text-gray-900 uppercase tracking-tighter mb-12"
            dangerouslySetInnerHTML={{ __html: t('services.cta.title') }}
          />
          <div className="flex justify-center">
            <a 
              href="/contact" 
              className="group flex items-center gap-6"
            >
              <span className="font-display text-[10px] tracking-[0.4em] text-gray-900 uppercase">
                {t('services.cta.button')}
              </span>
              <div className="w-16 h-16 rounded-full border border-gray-900 flex items-center justify-center group-hover:bg-gray-900 group-hover:border-gray-900 transition-all duration-300">
                <i className="ri-arrow-right-line text-2xl text-gray-900 group-hover:text-white transition-colors"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
